import { registerSchema } from '@gl/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { FormField } from '@/components/FormField';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { AuthLayout } from './AuthLayout';

export function RegisterPage() {
  const { setSession } = useAuth();
  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: { name: '', email: '', password: '' },
  });

  const register = useMutation({
    mutationFn: api.auth.register,
    onSuccess: (session) => {
      setSession(session);
      navigate('/onboarding', { replace: true });
    },
  });

  const { errors } = form.formState;

  return (
    <AuthLayout
      title="Creá tu cuenta"
      subtitle="En 2 minutos tenés tu menú QR y tu caja andando."
      footer={
        <>
          ¿Ya tenés cuenta?{' '}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Ingresá
          </Link>
        </>
      }
    >
      <form className="grid gap-4" onSubmit={form.handleSubmit((v) => register.mutate(v))} noValidate>
        <FormField label="Tu nombre" htmlFor="name" error={errors.name?.message}>
          <Input id="name" autoComplete="name" aria-invalid={!!errors.name} {...form.register('name')} />
        </FormField>
        <FormField label="Email" htmlFor="email" error={errors.email?.message}>
          <Input id="email" type="email" autoComplete="email" inputMode="email" aria-invalid={!!errors.email} {...form.register('email')} />
        </FormField>
        <FormField label="Contraseña" htmlFor="password" error={errors.password?.message} hint="Mínimo 8 caracteres.">
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            aria-invalid={!!errors.password}
            {...form.register('password')}
          />
        </FormField>
        {register.error && <p className="text-sm text-destructive">{errorMessage(register.error)}</p>}
        <Button type="submit" size="lg" disabled={register.isPending}>
          {register.isPending && <Spinner />} Crear cuenta
        </Button>
      </form>
    </AuthLayout>
  );
}
